// Where a lead actually came from.
//
// The site used to read utm_source and utm_campaign off window.location at the
// moment the form was submitted. That answers the question only when someone
// lands on a campaign URL and fills in the form without going anywhere else.
// Land on /?utm_source=google, read two pages, then enquire from /contact and
// both fields are empty — which described most real visits, so most leads
// arrived with no campaign against them.
//
// This records the campaign when the visitor ARRIVES and keeps it:
//
//   first touch  — the campaign that introduced them, kept for 90 days in
//                  localStorage, never overwritten while it is alive. This is
//                  the one marketing spend should be credited against.
//   last touch   — the campaign on the current visit, in sessionStorage, which
//                  is what the ad platforms themselves report.
//
// It also keeps the click ids (gclid, fbclid, li_fat_id, msclkid and the two
// newer Google ones). Those are the only way to match a lead back to the
// specific click that was paid for — UTM tags are a label anyone can copy, a
// click id is issued by the platform and is what offline conversion imports
// are keyed on. None of this is possible without storing them.
//
// Client-safe: no server imports, all storage access guarded. Storage can
// throw in private mode or be blocked entirely, and analytics must never be
// the reason a form stops working.

/** The campaign tags, as the platforms write them. */
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

/** Paid-click identifiers. gbraid/wbraid are Google's iOS/app-privacy
 *  replacements for gclid and arrive instead of it, not alongside. */
const CLICK_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "li_fat_id",
  "msclkid",
  "ttclid",
] as const;

const ALL_KEYS = [...UTM_KEYS, ...CLICK_KEYS] as const;

export type Touch = {
  [K in (typeof ALL_KEYS)[number]]?: string;
} & {
  /** The page they arrived on, path only — no query, which would repeat the
   *  tags and can carry personal data. */
  landing_page?: string;
  /** External referrer only; same-site is navigation, not a source. */
  referrer?: string;
  /** ISO timestamp of the arrival. */
  at?: string;
};

export type Attribution = {
  first?: Touch;
  last?: Touch;
  /** Ties the lead to its session in the first-party analytics tables, so a
   *  lead can be traced back through the pages that produced it. */
  session_id?: string;
  visitor_id?: string;
};

const FIRST_KEY = "ts_attr_first";
const LAST_KEY = "ts_attr_last";
const FIRST_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

/** Values go into a database and a CRM; cap them so a crafted URL cannot
 *  push anything large through. */
const clip = (v: string) => v.slice(0, 200);

function readJson<T>(store: Storage | undefined, key: string): T | null {
  try {
    const raw = store?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(store: Storage | undefined, key: string, value: unknown) {
  try {
    store?.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode, blocked storage — attribution is best-effort */
  }
}

/** The campaign visible in the current URL, or null when there is none. */
function touchFromUrl(): Touch | null {
  const params = new URLSearchParams(window.location.search);
  const touch: Touch = {};
  let found = false;
  for (const k of ALL_KEYS) {
    const v = params.get(k);
    if (v) {
      touch[k] = clip(v);
      found = true;
    }
  }
  // A plain external referrer is a source too — a link from a partner site or
  // a press article is worth knowing about even with no tags on the URL.
  let ref: string | undefined;
  try {
    const r = document.referrer;
    if (r && new URL(r).hostname !== window.location.hostname) ref = clip(r);
  } catch {
    /* malformed referrer */
  }
  if (!found && !ref) return null;
  if (ref) touch.referrer = ref;
  touch.landing_page = clip(window.location.pathname);
  touch.at = new Date().toISOString();
  return touch;
}

/**
 * Record the campaign for this arrival. Safe to call on every route change:
 * it only writes when the URL actually carries something, so internal
 * navigation cannot overwrite a real campaign with an empty one.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  const touch = touchFromUrl();
  if (!touch) return;

  // Last touch is per visit, and a new campaign inside the same visit wins:
  // that is the one that brought them back to the form.
  writeJson(window.sessionStorage, LAST_KEY, touch);

  // First touch is written once and then left alone until it ages out. An
  // expiry matters: without one, a campaign from a year ago would still be
  // taking credit for an enquiry it had nothing to do with.
  const existing = readJson<Touch>(window.localStorage, FIRST_KEY);
  const stale =
    !existing?.at || Date.now() - new Date(existing.at).getTime() > FIRST_TTL_MS;
  if (stale) writeJson(window.localStorage, FIRST_KEY, touch);
}

/** Everything known about where this visitor came from, for a form payload. */
export function readAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  const out: Attribution = {};
  const first = readJson<Touch>(window.localStorage, FIRST_KEY);
  const last = readJson<Touch>(window.sessionStorage, LAST_KEY);
  if (first) out.first = first;
  if (last) out.last = last;
  try {
    // Written by components/SiteTracker under the same keys.
    out.session_id = window.sessionStorage.getItem("ts_sid") || undefined;
    out.visitor_id = window.localStorage.getItem("ts_vid") || undefined;
  } catch {
    /* storage blocked */
  }
  return out;
}

/** The two columns website_leads has always had, filled from the best touch
 *  available so existing admin views, filters and exports keep working. Last
 *  touch first — it is what the ad platforms report — falling back to first. */
export function legacyUtmFields(a: Attribution): {
  utm_source?: string;
  utm_campaign?: string;
} {
  return {
    utm_source: a.last?.utm_source ?? a.first?.utm_source,
    utm_campaign: a.last?.utm_campaign ?? a.first?.utm_campaign,
  };
}

/** One line for a notification or a list column: "google / cpc — gcc-q3". */
export function describeAttribution(a: Attribution | null | undefined): string {
  const t = a?.last ?? a?.first;
  if (!t) return "direct";
  const who = [t.utm_source, t.utm_medium].filter(Boolean).join(" / ");
  const campaign = t.utm_campaign ? ` — ${t.utm_campaign}` : "";
  const paid = CLICK_KEYS.find((k) => t[k]) ? " (paid click)" : "";
  if (who) return `${who}${campaign}${paid}`;
  if (t.referrer) {
    try {
      return `referral: ${new URL(t.referrer).hostname}`;
    } catch {
      return "referral";
    }
  }
  return "direct";
}
